import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { clp, RESOURCE_LABEL, todayCL } from "@/lib/format";
import { Btn, Card, PageTitle } from "@/components/admin/AdminUI";

const monthStart = () => todayCL().slice(0, 8) + "01";
const monthEnd = () => { const [y, m] = todayCL().split("-").map(Number); return new Date(y, m, 0).toISOString().slice(0, 10); };

const Stat = ({ k, v, testid }) => <Card><p className="text-xs text-muted-foreground">{k}</p><p className="mt-1 font-serif text-3xl" data-testid={testid}>{v}</p></Card>;

export default function AdminReports() {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(monthEnd());
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    if (!start || !end || start > end) { setErr("La fecha de inicio debe ser anterior a la de término."); return; }
    setErr("");
    api.get("/admin/reports", { params: { start, end } }).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [start, end]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = async () => {
    let r;
    try { r = await api.get("/admin/export.csv", { params: { start, end }, responseType: "blob" }); } catch (e) { setErr("No se pudo exportar. Revisa las fechas."); return; }
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = u; a.download = `reservas_${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(u);
  };
  return (
    <div data-testid="admin-reports-page">
      <PageTitle title="Reportes">
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40 bg-white" data-testid="report-start-input" />
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40 bg-white" data-testid="report-end-input" />
        <Btn variant="outline" onClick={exportCsv} data-testid="export-csv-btn"><Download className="h-4 w-4" />Exportar CSV (Excel)</Btn>
      </PageTitle>
      {err && <p className="mb-4 text-sm text-red-600" data-testid="report-error">{err}</p>}
      {d && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat k="Solicitudes" v={d.total_requests} testid="report-total" />
            <Stat k="Pendientes" v={d.pending_requests} testid="report-pending" />
            <Stat k="Ingresos confirmados" v={clp(d.confirmed_revenue)} testid="report-revenue" />
            <Stat k="Abonos / pagos recibidos" v={clp(d.deposits_received)} testid="report-deposits" />
            <Stat k="Saldos pendientes" v={clp(d.pending_balance)} testid="report-balance" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <p className="font-semibold mb-4">Ocupación (reservas confirmadas)</p>
              {Object.entries(d.occupancy).map(([k, v]) => (
                <div key={k} className="mb-3">
                  <div className="flex justify-between text-sm"><span>{RESOURCE_LABEL[k]}</span><span className="tabular-nums">{v}%</span></div>
                  <div className="h-2 rounded-full bg-[#F3EDE2] mt-1"><div className="h-2 rounded-full bg-[#5E6B52]" style={{ width: `${Math.min(100, v)}%` }} /></div>
                </div>
              ))}
            </Card>
            <Card>
              <p className="font-semibold mb-4">Experiencias más reservadas</p>
              <div className="h-64">
                <ResponsiveContainer><BarChart data={d.by_experience.map(([n, c]) => ({ n, c }))} layout="vertical" margin={{ left: 40 }}>
                  <XAxis type="number" allowDecimals={false} /><YAxis type="category" dataKey="n" width={150} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="c" name="Reservas" fill="#5E6B52" radius={6} />
                </BarChart></ResponsiveContainer>
              </div>
            </Card>
          </div>
          <Card>
            <p className="font-semibold mb-4">Reservas por día</p>
            <div className="h-64">
              <ResponsiveContainer><BarChart data={d.by_day.map(([day, c]) => ({ day: day.slice(5), c }))}>
                <XAxis dataKey="day" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} /><Tooltip /><Bar dataKey="c" name="Reservas" fill="#C86D51" radius={6} />
              </BarChart></ResponsiveContainer>
            </div>
          </Card>
          <Card>
            <p className="font-semibold mb-3">Por mes (confirmadas)</p>
            {d.by_month.map(([m, v]) => <p key={m} className="text-sm">{m}: {v.reservas} reservas · {clp(v.ingresos)}</p>)}
            {!d.by_month.length && <p className="text-sm text-muted-foreground">Sin reservas confirmadas en el período.</p>}
            <p className="mt-3 text-xs text-muted-foreground">Estados: {Object.entries(d.by_status).map(([k, v]) => `${k} ${v}`).join(" · ") || "—"}</p>
          </Card>
        </div>
      )}
    </div>
  );
}
